import {
	type DefaultImageProps,
	Heading,
	Icon,
	Image,
	type ImageProps,
	InputSearch,
	RadioGroup,
	type RadioProps,
	type Suggestions,
	Text
} from "@pamperedchef/design-system";
import type { HeadingTag } from "@/app/utilities/types";
import { StandaloneSearchBox, useJsApiLoader } from "@react-google-maps/api";
import { type RefObject, useEffect, useRef, useState } from "react";

import { useClickOutside } from "@/app/hooks/useClickOutside";
import * as styles from "../shared.module.scss";

export interface ConsultantItem<ImageComponentProps> {
	consultantId: string;
	consultantPk: string;
	consultantName: string;
	consultantImage?: ImageProps<ImageComponentProps>;
	location: string;
	activePWS: boolean;
}

export interface FindConsultantProps<ImageComponentProps = DefaultImageProps> {
	defaultFilter?: string;
	headline?: string;
	headlineTag?: HeadingTag;
	inputLabel?: string;
	inputPlaceholder?: string;
	suggestions?: {
		label?: string;
		items?: ConsultantItem<ImageComponentProps>[];
		loading: boolean;
		noResultsMessage?: Suggestions["noResultsMessage"];
	};
	legend?: string;
	filters?: RadioProps[];
	value?: string;
	selectedFilter?: string;
	onLocationSearch?: () => void;
	onSelection?: (key: string) => void;
	onInputChange?: (value: string, name?: string) => void;
	onFilterChange?: (value: string) => void;
	googleMapsApiKey: string;
}

/**
 * FindConsultant Component
 */
export function FindConsultant<ImageComponentProps = DefaultImageProps>({
	headline,
	headlineTag = "h4",
	inputLabel,
	inputPlaceholder,
	suggestions,
	legend,
	filters = [],
	value = "",
	defaultFilter,
	selectedFilter,
	onLocationSearch,
	onSelection,
	onInputChange,
	onFilterChange,
	googleMapsApiKey
}: FindConsultantProps<ImageComponentProps>): JSX.Element {
	const [isPopoverVisible, setIsPopoverVisible] = useState(false);
	const popverRef = useRef<HTMLInputElement>(null);
	const locationRef = useRef(null);
	const isOutsideClick = useClickOutside(popverRef);

	const hasLocationFilter = selectedFilter?.includes("location");

	const { isLoaded } = useJsApiLoader({
		id: "google-map-script",
		googleMapsApiKey: googleMapsApiKey as string,
		libraries: ["places"]
	});

	/**
	 * Change handler
	 */
	function handleInputChange(val: string): void {
		onInputChange?.(val);
	}

	/**
	 * location handler
	 */
	function handleOnLocationChanged(): void {
		const address = (locationRef.current as any).getPlaces();

		const {
			geometry: {
				location: { lat, lng }
			},
			formatted_address: locationName
		} = address[0];

		onInputChange?.(`${lat()},${lng()}`, locationName);
	}

	/**
	 * Map suggestions data
	 * @param consultants
	 */
	function mapConsultantsToSuggestions(
		consultants: ConsultantItem<ImageComponentProps>[]
	): Suggestions {
		return {
			items: consultants.map((consultant) => ({
				url: consultant?.consultantId,
				label: consultant?.consultantName,
				supportiveText: consultant?.location
			})),
			visible: isPopoverVisible,
			loading: suggestions?.loading || false,
			noResultsMessage: "No results found",
			className: "consultant-suggestions",
			motionProps: {},
			onSelection: (key): void => {
				onSelection?.(key);
			}
		};
	}

	useEffect(() => {
		if (value.length > 2) {
			setIsPopoverVisible(true);
		}

		if (isOutsideClick) {
			setIsPopoverVisible(false);
		}
	}, [isOutsideClick, value]);

	return (
		<div className={styles["find-form"]}>
			<Heading className={styles["find-form__title"]} tag={headlineTag}>
				{headline}
			</Heading>

			<RadioGroup
				aria-label="Filter"
				className={styles["find-form__filters"]}
				items={filters}
				inline
				defaultValue={defaultFilter}
				onChange={(val) => onFilterChange?.(val as string)}
			/>

			<Text className={styles["find-form__label"]} styledAs="smr">
				{inputLabel}
			</Text>

			{hasLocationFilter ? (
				isLoaded && (
					<StandaloneSearchBox
						onLoad={(ref) => (locationRef.current = ref as never)}
						onPlacesChanged={handleOnLocationChanged}
					>
						<InputSearch
							aria-label={inputLabel}
							className={styles["find-form__input"]}
							onSearchClick={() => onLocationSearch?.()}
							placeholder={inputPlaceholder}
							suggestions={mapConsultantsToSuggestions(
								suggestions?.items || []
							)}
							value={value}
							onChange={handleInputChange}
							customRef={popverRef as RefObject<HTMLInputElement>}
						/>
					</StandaloneSearchBox>
				)
			) : (
				<InputSearch
					aria-label={inputLabel}
					id="consultant-search"
					className={styles["find-form__input"]}
					onSearchClick={() => onLocationSearch?.()}
					placeholder={inputPlaceholder}
					suggestions={mapConsultantsToSuggestions(
						suggestions?.items || []
					)}
					value={value}
					onChange={handleInputChange}
					customRef={popverRef as RefObject<HTMLInputElement>}
				/>
			)}

			<Text className={styles["find-form__legend"]} styledAs="xsr">
				{legend}
			</Text>
		</div>
	);
}
